import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../components/admin/SuperAdminLayout';
import { PlusIcon, TrashIcon, PencilIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useHybridAuth } from '../../contexts/HybridAuthContext';
import { apiFetch } from '../../utils/apiFetch';

const updateAdmin = async (id: string, payload: Record<string, string>) => {
  const response = await apiFetch(`/api/admin/admins/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error(`Update failed with status ${response.status}`);
};

interface Admin {
  id: string;
  name: string;
  email: string;
  role: string;
  lastLogin: string | null;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

interface AdminStats {
  total_admins: number;
  total_super_admins: number;
  total_regular_admins: number;
  active_admins: number;
  inactive_admins: number;
  pending_admins: number;
}

const AdminsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, impersonate } = useHybridAuth();
  const canImpersonateAdmins = user?.role === 'super_admin';
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    total_admins: 0,
    total_super_admins: 0,
    total_regular_admins: 0,
    active_admins: 0,
    inactive_admins: 0,
    pending_admins: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    role: '',
    status: 'active' as 'active' | 'inactive' | 'pending'
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      // The page has no pager, so request the endpoint's maximum page size.
      const response = await apiFetch('/api/admin/admins?limit=100');
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
      const data = await response.json();

      setAdmins(data.admins || []);
      setStats(data.stats || {
        total_admins: 0,
        total_super_admins: 0,
        total_regular_admins: 0,
        active_admins: 0,
        inactive_admins: 0,
        pending_admins: 0
      });
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to load admin data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonateAdmin = async (admin: Admin) => {
    if (!confirm(`Impersonate ${admin.name} (${admin.email})? This is recorded in the admin activity log.`)) {
      return;
    }
    if (await impersonate(String(admin.id))) {
      navigate('/dashboard');
    }
  };

  const handleEditAdmin = (admin: Admin) => {
    setSelectedAdmin(admin);
    setEditFormData({
      name: admin.name,
      email: admin.email,
      role: admin.role,
      status: admin.status
    });
    setShowEditModal(true);
  };

  const handleDeleteAdmin = (admin: Admin) => {
    setSelectedAdmin(admin);
    setShowDeleteConfirm(true);
  };

  const handleUpdateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAdmin) return;

    try {
      await updateAdmin(selectedAdmin.id, {
        name: editFormData.name,
        email: editFormData.email,
        role: editFormData.role,
        status: editFormData.status
      });
      
      setShowEditModal(false);
      setSelectedAdmin(null);
      fetchAdmins(); // Refresh the list
    } catch (err) {
      console.error('Error updating admin:', err);
      setError('Failed to update admin. Please try again.');
    }
  };

  const handleSoftDeleteAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      await updateAdmin(selectedAdmin.id, {
        status: 'inactive'
      });
      
      setShowDeleteConfirm(false);
      setSelectedAdmin(null);
      fetchAdmins(); // Refresh the list
    } catch (err) {
      console.error('Error deactivating admin:', err);
      setError('Failed to deactivate admin. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30';
      case 'inactive':
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
      case 'pending':
        return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
      default:
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
      case 'admin':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      default:
        return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      default:
        return role;
    }
  };

  const getAvatarColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'from-purple-500 to-pink-500';
      case 'admin':
        return 'from-indigo-500 to-blue-500';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Admin Management</h2>
            <p className="text-sm text-slate-400 mt-1">
              Manage administrator accounts and permissions
            </p>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Add new admin</span>
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl shadow-xl border border-slate-700/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Total Admins</p>
                <p className="text-2xl font-bold text-white">{stats.total_admins}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl shadow-xl border border-slate-700/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-sm">SA</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Super Admins</p>
                <p className="text-2xl font-bold text-white">{stats.total_super_admins}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl shadow-xl border border-slate-700/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Active</p>
                <p className="text-2xl font-bold text-white">{stats.active_admins}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl shadow-xl border border-slate-700/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-sm">⏸</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Inactive</p>
                <p className="text-2xl font-bold text-white">{stats.inactive_admins}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-900/50 backdrop-blur-sm rounded-xl shadow-xl border border-red-700/50 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">!</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Error Loading Data</h3>
                  <p className="text-sm text-red-200">{error}</p>
                </div>
              </div>
              <button 
                onClick={fetchAdmins}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Admins Table */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl shadow-xl border border-slate-700/50">
          <div className="p-6 border-b border-slate-700/50">
            <h3 className="text-lg font-medium text-white">Administrator Accounts</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Admin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {loading ? (
                  // Loading skeleton rows
                  Array.from({ length: 3 }).map((_, index) => (
                    <tr key={`loading-${index}`} className="animate-pulse">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-slate-600 rounded-full"></div>
                          <div className="ml-4 space-y-2">
                            <div className="h-4 bg-slate-600 rounded w-24"></div>
                            <div className="h-3 bg-slate-600 rounded w-32"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-600 rounded w-24"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-6 bg-slate-600 rounded-full w-16"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-600 rounded w-20"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-slate-600 rounded w-20"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <div className="h-4 w-4 bg-slate-600 rounded"></div>
                          <div className="h-4 w-4 bg-slate-600 rounded"></div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : admins.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center">
                      <div className="text-slate-400">
                        <p className="text-lg font-medium">No Administrators Found</p>
                        <p className="text-sm mt-1">There are currently no admin users in the system.</p>
                      </div>
                    </td>
                  </tr>
                ) : admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-slate-700/30 transition-colors duration-200">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 bg-gradient-to-br ${getAvatarColor(admin.role)} rounded-full flex items-center justify-center`}>
                          <span className="text-sm font-medium text-white">
                            {admin.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{admin.name}</div>
                          <div className="text-sm text-slate-400">{admin.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getRoleColor(admin.role)}`}>
                        {getRoleLabel(admin.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(admin.status)}`}>
                        {admin.status.charAt(0).toUpperCase() + admin.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      {admin.lastLogin ? new Date(admin.lastLogin).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">
                      {new Date(admin.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {canImpersonateAdmins && String(admin.id) !== String(user?.id) && (
                          <button
                            onClick={() => handleImpersonateAdmin(admin)}
                            className="text-amber-400 hover:text-amber-300 transition-colors duration-200"
                            title="Impersonate admin"
                          >
                            <UserCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditAdmin(admin)}
                          className="text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
                          title="Edit admin"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteAdmin(admin)}
                          className="text-red-400 hover:text-red-300 transition-colors duration-200"
                          title="Deactivate admin"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Admin Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md mx-4 border border-slate-700/50">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-white">Add New Administrator</h3>
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <span className="sr-only">Close</span>
                  ✕
                </button>
              </div>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                  <input
                    type="text"
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter full name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input
                    type="email"
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter email address"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
                  <select
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select role</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                  <input
                    type="password"
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter password"
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Add Administrator
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Admin Modal */}
        {showEditModal && selectedAdmin && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md mx-4 border border-slate-700/50">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-white">Edit Administrator</h3>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <span className="sr-only">Close</span>
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleUpdateAdmin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
                  <select
                    value={editFormData.role}
                    onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Status</label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({...editFormData, status: e.target.value as 'active' | 'inactive'})}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                
                <div className="bg-slate-700/30 rounded-lg p-4">
                  <p className="text-xs text-slate-400 mb-2">Security Note:</p>
                  <p className="text-xs text-slate-500">
                    Password changes require the admin to reset their password via email for security reasons.
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Update Administrator
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && selectedAdmin && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md mx-4 border border-slate-700/50">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mr-4">
                  <TrashIcon className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Deactivate Administrator</h3>
                  <p className="text-sm text-slate-400">This action cannot be undone easily</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-slate-300 mb-4">
                  Are you sure you want to deactivate <strong>{selectedAdmin.name}</strong>?
                </p>
                <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-amber-900 text-xs font-bold">!</span>
                    </div>
                    <div>
                      <p className="text-amber-200 text-sm font-medium mb-1">Important:</p>
                      <p className="text-amber-300 text-sm">
                        This will mark the administrator as inactive. They will no longer be able to access the admin panel, 
                        but their account data will be preserved.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSoftDeleteAdmin}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Deactivate Administrator
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default AdminsPage;